import React, { useCallback, useEffect, useState } from "react";
import { fetchAllCardPaymentMethods, initializeNewPaymentMethodSetup } from "api/PaymentApiService";
import styles from "styles/components/PaymentMethods.module.sass";
import { LowLevelPaymentMethod } from "interface/LowLevelPaymentMethod";
import CardPaymentMethod from "components/cards/CardPaymentMethod";
import AddPaymentCard from "components/cards/AddPaymentCard";
import { useToast } from "provider/ToastProvider";
import AddPaymentMethodModal from "components/cards/AddPaymentMethodModal";
import ZeroneLoader from "components/common/ZeroneLoader";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const tag = 'DEFAULT_PAYMENT_METHOD';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')

/**
 * PaymentMethods component - Displays the user's default payment method.
 * 
 * This component fetches and displays the user's default payment method, including card brand, last four digits, expiration date, and type.
 * If no default payment method is set, it informs the user accordingly.
 * 
 * @component
 * <PaymentMethods />
 * 
 * @returns {JSX.Element} PaymentMethods component
 */
const PaymentMethods: React.FC = () => {
    const [paymentMethods, setPaymentMethods] = useState<LowLevelPaymentMethod[] | null>(null);
    const [changeDetected, setChangeDetected] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { addToast } = useToast();

    const loadPaymentMethods = useCallback(async () => {
        try {
            const methods = await fetchAllCardPaymentMethods(tag);  
            setPaymentMethods(methods ?? []);
        } catch {
            setPaymentMethods(null);
        } finally {
            setChangeDetected(false);
        }
    }, []);

    // Load payment methods on component mount and when changeDetected changes
    useEffect(() => {
        loadPaymentMethods();
    }, [loadPaymentMethods]);

    useEffect(() => {
        if (!changeDetected) return;

        addToast({
            type: 'info',
            message: 'Changes detected, refreshing payment methods...',
        });

        (async () => {
            await loadPaymentMethods();
        })();

    }, [changeDetected, loadPaymentMethods, addToast]);

    const handleAddPaymentMethod = useCallback(async () => {
        if (paymentMethods && paymentMethods.length >= 2) {
            addToast({ type: 'warning', message: 'You can only have up to 2 payment methods.' });
            return;
        }

        setIsLoading(true);
        try {
            const secret = await initializeNewPaymentMethodSetup(tag);
            setClientSecret(secret);
            setIsModalOpen(true);
        } catch {
            addToast({ type: 'error', message: 'Failed to start payment method setup.' });
        } finally {
            setIsLoading(false);
        }
    }, [addToast, paymentMethods, setClientSecret, setIsModalOpen]);

    return (
        <>
        <div className={styles['payment-methods-container']}>
            {paymentMethods && paymentMethods.length > 0 && paymentMethods.map((method) => (
                <CardPaymentMethod key={method.id} paymentMethod={method} knownPaymentMethodsCount={paymentMethods.length} setChangeDetected={setChangeDetected} />
            ))}
            {(paymentMethods && paymentMethods.length < 2) && (
                <AddPaymentCard onAddPaymentMethod={() => { void handleAddPaymentMethod(); }} />
            )}
        </div>

        {isModalOpen && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: clientSecret ?? '' }}>
                <AddPaymentMethodModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    clientSecret={clientSecret ?? ''}
                    onPaymentMethodAdded={() => setChangeDetected(true)}
                />
            </Elements>
        )}

        {isLoading && (
            <div className={styles['loading-overlay']}>
                <ZeroneLoader text="Preparing payment method setup..." />
            </div>
        )}
    </>
    );
};

export default PaymentMethods;
